import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  FixedBottomBarContainer,
  NoddiBasicCard,
  NoddiInfoBanner,
  type ScreenBaseWrapperWidth,
  useIsWithinTailWindBreakpoint
} from 'noddi-ui-web';
import { formatPhoneNumber } from 'noddi-util';
import { type SMSLoginStepLayout, useAuthContext } from 'noddi-web';
import type { PropsWithChildren, ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useOpenUserGroupPickerAfterLogin } from '../../hooks/useOpenUserGroupPickerAfterLogin';
import type { BookingFlowProgress } from '../BookingFlow/Common/bookingFlowProgress';
import { BookingFlowScreen } from '../BookingFlow/Common/BookingFlowScreen';
import { NavioContactChatWidget } from '../BookingFlow/Common/NavioContactChatWidget';
import { ContentWrapperCustomerApp } from '../Layouts/ContentWrapperCustomerApp';
import { FlowBackButton } from '../Layouts/FlowBackButton';
import { type CheckoutLoginOrder, CheckoutLoginOrderRow, CheckoutLoginOrderRowSkeleton } from './CheckoutLoginOrderRow';
import { CustomerSMSLogin } from './CustomerSMSLogin';

type ScreenProps = {
  /** Previous step; history back when omitted. */
  backPath?: string;
  /** Booking steps keep the booking flow's own header, so the title does not jump between steps. */
  isBookingFlow?: boolean;
  /** Booking flow only: the step this login stands in for in the flow's progress bar. */
  progress?: BookingFlowProgress;
  width?: ScreenBaseWrapperWidth;
  /** Same width as the flow's other steps, so the page does not jump. */
  className?: string;
  /** Booking prices drop by account discounts once logged in; offers and proposals are priced already. */
  showsAccountDiscountNotice?: boolean;
};

type Props = ScreenProps & {
  /** Where checkout continues once logged in. A visitor who is logged in already goes straight there. */
  nextPath: string;
  order?: CheckoutLoginOrder;
  /** Shows a placeholder where the order row lands; a failed order just leaves it out. */
  isOrderLoading?: boolean;
  /** Above the login on every step, e.g. how long a held time stays reserved. */
  notice?: ReactNode;
  /** Replaces the login when checkout cannot continue, e.g. the held time expired. */
  blockedBy?: ReactNode;
  /** The group the flow belongs to — resolves a shared phone to one account. */
  userGroupId?: number;
  /** Lets an account with several profiles pick one. Proposals own their group, so they skip it. */
  opensUserGroupPicker?: boolean;
  /** Runs once the SMS code logs in, before moving on, e.g. to refetch data that differs for a logged-in visitor. */
  onLoggedIn?: () => void;
};

/**
 * Login as its own checkout step in front of the confirm page, so that page is review-and-pay only.
 * Each SMS step is a page of its own — number, then code — with its submit in the footer beside Back and Chat.
 */
export const CheckoutLoginStep = ({
  nextPath,
  order,
  isOrderLoading,
  notice,
  blockedBy,
  userGroupId,
  opensUserGroupPicker,
  onLoggedIn,
  ...screenProps
}: Props) => {
  const { isLoggedIn } = useAuthContext();
  const openUserGroupPickerAfterLogin = useOpenUserGroupPickerAfterLogin();
  const navigate = useNavigate();

  if (isLoggedIn) {
    return <Navigate to={nextPath} replace />;
  }

  if (blockedBy) {
    return (
      <CheckoutLoginScreen {...screenProps}>
        {notice}
        {blockedBy}
      </CheckoutLoginScreen>
    );
  }

  return (
    <CustomerSMSLogin
      userGroupId={userGroupId}
      hidePhoneNumberText
      phoneNumberLabel={t`Mobile number`}
      phoneStepFooter={
        // On a phone this sits straight on the tinted page, where `text-muted-foreground` all but vanishes.
        <p className='text-sm text-foreground/70'>
          <Trans>New customer? You add your name and email after the code.</Trans>
        </p>
      }
      onLoginSuccess={(userData) => {
        if (opensUserGroupPicker) {
          openUserGroupPickerAfterLogin(userData);
        }
        onLoggedIn?.();
        // Replace: a logged-in visitor is bounced off this step, so it must never be a Back target.
        navigate(nextPath, { replace: true });
      }}
      renderStep={(step) => (
        <CheckoutLoginScreen {...screenProps} step={step}>
          {notice}
          <LoginFields>{step.content}</LoginFields>
          {step.kind === 'phone' &&
            (order ? (
              <CheckoutLoginOrderRow order={order} isPriceBeforeLogin={screenProps.showsAccountDiscountNotice} />
            ) : (
              isOrderLoading && <CheckoutLoginOrderRowSkeleton />
            ))}
          {step.kind === 'phone' && screenProps.showsAccountDiscountNotice && (
            // `brand`'s tint matches the booking flow's page background and vanishes on it; a card surface keeps it neutral.
            <NoddiInfoBanner
              variant='brand'
              size='small'
              className='bg-card'
              title={t`Have discounts on your account? They are applied once you log in.`}
            />
          )}
        </CheckoutLoginScreen>
      )}
    />
  );
};

type ScreenStep = Pick<SMSLoginStepLayout, 'kind' | 'phoneNumber' | 'submit'>;

const CheckoutLoginScreen = ({
  step,
  backPath,
  isBookingFlow,
  progress,
  width,
  className,
  showsAccountDiscountNotice,
  children
}: PropsWithChildren<ScreenProps & { /** Unset while the login is blocked. */ step?: ScreenStep }>) => {
  const title = step?.kind === 'code' ? t`Enter the code` : t`Log in to continue`;
  const phoneNumber = formatPhoneNumber(step?.phoneNumber ?? '');
  const subtitle =
    step &&
    {
      phone: showsAccountDiscountNotice
        ? t`We send you a one-time code by SMS. If you have a discount with us, you will see the updated price in the next step.`
        : t`We send you a one-time code by SMS.`,
      code: (
        <Trans>
          We sent a 6-digit code to <strong className='font-semibold text-primary'>{phoneNumber}</strong>.
        </Trans>
      ),
      accountSelection: undefined,
      signup: undefined
    }[step.kind];

  const body = <div className='flex flex-col gap-4'>{children}</div>;
  // Outside the booking flow the bar spans the page's content width, as on the steps around it.
  const renderFooter = (contentWidth?: ScreenBaseWrapperWidth) => (
    <FixedBottomBarContainer contentWidth={contentWidth}>
      <div className='flex items-center gap-2'>
        <FlowBackButton backPath={backPath} />
        <NavioContactChatWidget />
      </div>
      {step?.submit}
    </FixedBottomBarContainer>
  );

  if (isBookingFlow) {
    return (
      <BookingFlowScreen
        title={title}
        progress={progress}
        description={subtitle}
        hideBackButton
        width={width}
        customBottomBar={renderFooter()}
      >
        {body}
      </BookingFlowScreen>
    );
  }

  return (
    <ContentWrapperCustomerApp title={title} subtitle={subtitle} hideBackButton width={width} className={className}>
      {body}
      {renderFooter(width ?? 'md')}
    </ContentWrapperCustomerApp>
  );
};

/** The fields sit on the page on a phone and in a card on wider screens, as in the design. */
const LoginFields = ({ children }: PropsWithChildren) => {
  const isWithinMd = useIsWithinTailWindBreakpoint('md');

  if (isWithinMd) {
    return <div className='flex flex-col gap-4'>{children}</div>;
  }
  return <NoddiBasicCard className='flex flex-col gap-4'>{children}</NoddiBasicCard>;
};
